/**
 * Your call — a private pre-match W/D/L choice. Pending choices are visible,
 * but only a choice acknowledged by COYG can become a shareable device record.
 */
import { useEffect, useRef, useState } from 'react';
import { fallbackPrediction, fetchPrediction, postPrediction, PREDICTION_EMPTY } from '../lib/api';
import { broadcastFanReaction } from '../lib/intents';
import { haptic } from './Floodlight';
import type { PredictionPick, PredictionSummary } from '../lib/types';
import { ParticipationGate, useParticipationConsent } from './ParticipationGate';
import { ShareButton } from './ShareButton';
import { matchLink } from '../lib/share';
import { recordGrowth } from '../lib/growth';
import './call-receipt.css';

const PICK_LABEL: Record<PredictionPick, string> = { W: 'Win', D: 'Draw', L: 'Loss' };

export const PredictionStrip = ({ matchId, matchLabel = 'Arsenal matchday' }: { matchId: string; matchLabel?: string }) => {
  const [summary, setSummary] = useState<PredictionSummary>(() => fallbackPrediction(matchId));
  const live = useRef(true);
  const writeGeneration = useRef(0);
  const loadGeneration = useRef(0);
  const matchRef = useRef(matchId);
  matchRef.current = matchId;
  const [hydrated, setHydrated] = useState(false);
  const [participationAllowed, acceptParticipation] = useParticipationConsent();
  const [gateOpen, setGateOpen] = useState(false);
  const [pendingPick, setPendingPick] = useState<PredictionPick | null>(null);
  const [saving, setSaving] = useState<{ pick: PredictionPick | null } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    live.current = true;
    setHydrated(false);
    setSummary(fallbackPrediction(matchId));
    setSaving(null);
    setNotice(null);
    setGateOpen(false);
    setPendingPick(null);
    const load = ++loadGeneration.current;
    const generation = writeGeneration.current;
    fetchPrediction(matchId).then(({ prediction }) => {
      if (live.current && load === loadGeneration.current && generation === writeGeneration.current) {
        setSummary(prediction);
        setHydrated(true);
      }
    });
    return () => {
      live.current = false;
    };
  }, [matchId]);

  useEffect(() => {
    const clear = () => {
      writeGeneration.current += 1; loadGeneration.current += 1;
      setSummary(PREDICTION_EMPTY); setSaving(null); setNotice(null);
      setGateOpen(false); setPendingPick(null);
    };
    window.addEventListener('coyg:local-data-cleared', clear);
    return () => window.removeEventListener('coyg:local-data-cleared', clear);
  }, []);

  useEffect(() => {
    const reconcile = (event: Event) => {
      if ((event as CustomEvent<{ kind?: string }>).detail?.kind !== 'prediction') return;
      const reconcileMatch = matchId;
      const load = loadGeneration.current;
      const write = writeGeneration.current;
      void fetchPrediction(matchId).then(({ prediction }) => {
        if (live.current && reconcileMatch === matchRef.current && load === loadGeneration.current && write === writeGeneration.current) { setSummary(prediction); setHydrated(true); }
      });
    };
    window.addEventListener('coyg:write-persistence-failed', reconcile);
    return () => window.removeEventListener('coyg:write-persistence-failed', reconcile);
  }, [matchId]);

  const commitPick = (p: PredictionPick) => {
    if (!hydrated || saving) return;
    const write = ++writeGeneration.current;
    const writeMatch = matchId;
    haptic();
    const next = summary.mine === p ? null : p;
    const previous = summary;
    setSaving({ pick: next });
    setNotice(null);
    postPrediction(matchId, next).then((res) => {
      if (!live.current || write !== writeGeneration.current || writeMatch !== matchRef.current) return;
      setSaving(null);
      setSummary(res ?? previous);
      if (!res) {
        setNotice('COYG could not confirm that change. Your last confirmed call is shown. Check your connection and whether kick-off has passed.');
        return;
      }
      setNotice(res.mine ? `${PICK_LABEL[res.mine]} call saved. Tap it again to withdraw before kick-off.` : 'Your call has been withdrawn.');
      if (res.mine) recordGrowth('prediction', 'match');
      broadcastFanReaction(matchId, 'prediction');
    }).catch(() => {
      if (!live.current || write !== writeGeneration.current || writeMatch !== matchRef.current) return;
      setSaving(null);
      setSummary(previous);
      setNotice('COYG could not confirm that change. Your last confirmed call is shown. Try again when connected.');
    });
  };

  const pick = (p: PredictionPick) => {
    if (!hydrated || saving) return;
    if (!participationAllowed) {
      setPendingPick(p);
      setGateOpen(true);
      return;
    }
    commitPick(p);
  };

  return (
    <div className="predict" aria-busy={Boolean(saving)}>
      <div className="predict-row" role="group" aria-label="Call your shot: Arsenal result">
        {(['W', 'D', 'L'] as PredictionPick[]).map((p) => (
          <button
            key={p}
            type="button"
            className={`predict-btn${(saving ? saving.pick : summary.mine) === p ? ' active' : ''}`}
            aria-pressed={(saving ? saving.pick : summary.mine) === p}
            onClick={() => pick(p)}
            disabled={!hydrated || Boolean(saving)}
          >
            {PICK_LABEL[p]}
          </button>
        ))}
      </div>
      <div className="predict-meta">
        <span className="predict-caption" role="status" aria-live="polite">
          {!hydrated ? 'Loading your call…' : saving ? saving.pick ? `Saving your ${PICK_LABEL[saving.pick].toLowerCase()} call…` : 'Withdrawing your call…' : notice ?? (summary.mine ? 'Your saved call is a personal record on this device. Change or withdraw it before kick-off.' : 'Call Arsenal’s result before kick-off. Your receipt updates when a confirmed final result arrives.')}
        </span>
      </div>
      {hydrated && !saving && summary.mine && (
        <div className="gauge-share">
          <ShareButton
            ghost
            label="Share my call"
            moment={{
              title: 'COYG — my match call',
              text: `My call for ${matchLabel}: ${PICK_LABEL[summary.mine]}. Personal record from my COYG device.`,
              url: matchLink(matchId),
              card: {
                eyebrow: 'MY MATCHDAY CALL',
                headline: PICK_LABEL[summary.mine],
                detail: matchLabel,
                stamp: 'MY DEVICE RECORD · COYG.ORG',
                personal: true,
              },
            }}
          />
        </div>
      )}
      {gateOpen && (
        <ParticipationGate
          action="make your match call"
          onAccepted={acceptParticipation}
          onComplete={() => { if (pendingPick) commitPick(pendingPick); }}
          onClose={() => { setGateOpen(false); setPendingPick(null); }}
        />
      )}
    </div>
  );
};
